// src/components/AppShell.jsx
import React, { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApp } from '../context/AppContext'
import { ToastContainer } from './UI'
import UserManager from './UserManager'
import Footer from './Footer'
import LanguageSwitcher from './LanguageSwitcher'

export default function AppShell() {
  const { currentUser, currentProfile, logout, dataLoading } = useApp()
  const { t } = useTranslation()
  const [showUsers, setShowUsers] = useState(false)
  const currentPlan = currentProfile?.plan || 'free'

  if (!currentUser) return null

  const displayName    = currentProfile?.name || currentUser.email?.split('@')[0] || 'User'
  const avatarInitials = currentProfile?.initials || displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  const avatarColor    = currentProfile?.color || '#1c3a5e'
  const isAdmin        = currentProfile?.role === 'admin'

  return (
    <div className="app-shell">
      <header className="topbar" role="banner">
        <NavLink to="/" className="topbar-brand" aria-label="TecFin home">
          <div className="brand-icon"><i className="ti ti-wallet" aria-hidden="true" /></div>
          {t('app.name')}
        </NavLink>

        <nav aria-label="Main navigation" style={{ display: 'flex' }}>
          <NavLink to="/transactions" className={({ isActive }) => `nav-tab${isActive ? ' active' : ''}`}>
            <i className="ti ti-plus-minus" aria-hidden="true" /><span>{t('nav.transactions')}</span>
          </NavLink>
          <NavLink to="/ledger" className={({ isActive }) => `nav-tab${isActive ? ' active' : ''}`}>
            <i className="ti ti-book-2" aria-hidden="true" /><span>{t('nav.ledger')}</span>
          </NavLink>
          <NavLink to="/accounts" className={({ isActive }) => `nav-tab${isActive ? ' active' : ''}`}>
            <i className="ti ti-credit-card" aria-hidden="true" /><span>{t('nav.accounts')}</span>
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => `nav-tab${isActive ? ' active' : ''}`}>
            <i className="ti ti-chart-pie" aria-hidden="true" /><span>{t('nav.reports')}</span>
          </NavLink>
        </nav>

        <div className="topbar-end">
          {dataLoading && (
            <i className="ti ti-loader-2" style={{ color: 'var(--brand-400)', fontSize: 16, animation: 'spin 1s linear infinite' }} aria-hidden="true" />
          )}

          {/* Language switcher */}
          <LanguageSwitcher variant="topbar" />

          <NavLink to="/pricing" style={{
            fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
            textDecoration: 'none', letterSpacing: 0.3, textTransform: 'uppercase',
            background: currentPlan === 'free' ? 'rgba(255,255,255,0.08)' : 'var(--brand-600)',
            color: currentPlan === 'free' ? '#a8c4f0' : '#fff',
            border: currentPlan === 'free' ? '1px solid rgba(255,255,255,0.15)' : 'none',
          }}>
            {currentPlan === 'free'
              ? <><i className="ti ti-rocket" aria-hidden="true" /> {t('nav.upgrade')}</>
              : currentPlan
            }
          </NavLink>

          <span className="topbar-uname">{displayName}</span>
          <div className="user-avatar" style={{ background: avatarColor }} title={displayName}>
            {avatarInitials}
          </div>
          {isAdmin && (
            <button className="topbar-btn" onClick={() => setShowUsers(true)}>
              <i className="ti ti-users" aria-hidden="true" /><span>{t('nav.users')}</span>
            </button>
          )}
          <button className="topbar-btn" onClick={logout} aria-label={t('nav.logout')}>
            <i className="ti ti-logout" aria-hidden="true" /><span>{t('nav.logout')}</span>
          </button>
        </div>
      </header>

      <main className="page-content" id="main-content" role="main">
        <Outlet />
      </main>

      <Footer />

      {isAdmin && <UserManager open={showUsers} onClose={() => setShowUsers(false)} />}
      <ToastContainer />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
